import { fetchArticleBreadcrumbs } from "../../reducers/breadcrumbSlice" 
import { concatUnassignedArticles } from "../../reducers/unassignedArticlesEventsSlice" 
import { addNoteToTree, deleteNoteFromTree, fetchUniqueCounts, getClosestParentNoteByPath, getNoteByPath, moveNoteInTree, openToPath, updateNoteContent } from "../../reducers/workspaceNoteSlice"
import {openPrimaryWorkspace, openSecondaryWorkspace} from "../../reducers/workspaceSlice"
import axios from "axios"
import {updateSearches} from "../../reducers/workspaceSearchSlice"
import note from "../../components/workspace/note_tree/Note"

export const getCurrentUserId = (storeAPI) => {
	const state = storeAPI.getState()
	const currentUserId = state.user?.value?.id
	return currentUserId
}

const updateBreadcrumbs = (storeAPI, note) => {
	const state = storeAPI.getState()
	const {workspaceId} = note
	const trackedBreadcrumb = state.breadcrumbSlice.byWorkspace[workspaceId]?.trackedBreadcrumb

	if (trackedBreadcrumb) {
		const isNoteIdInTrackedBreadcrumbs = trackedBreadcrumb.breadcrumbTopLevelNoteIds
		const isIncomingNoteInBreadcrumbPath = note.path
			.split(".")
			.map(Number)
			.some(number => isNoteIdInTrackedBreadcrumbs.includes(number))
		if (isIncomingNoteInBreadcrumbPath) {
			storeAPI.dispatch(fetchArticleBreadcrumbs({article: {id: trackedBreadcrumb.articleId}, workspaceId}))
		}
	}
}

const checkForAndUpdateWorkspaceNoteSearches = async (storeAPI, note) => {
	const state = storeAPI.getState()
	// check for existing workspace searches in either primary or secondary workspace
	const workspaceSearches = state.workspaceSearch.byWorkspace
	// if searches exist, update them so search result notes match existing permissions...
	if (workspaceSearches[note.workspaceId]) await storeAPI.dispatch(updateSearches(note.workspaceId))
}

export const notesSocketMiddleware = (socket) => {
	return storeAPI => {

		socket.on("UPDATE_NOTE", async ({note}) => {
			storeAPI.dispatch(updateNoteContent(note))
			updateBreadcrumbs(storeAPI, note)
			await checkForAndUpdateWorkspaceNoteSearches(storeAPI, note)
		})

		socket.on("ADD_NOTE", async ({note, userId}) => {
			const state = storeAPI.getState()
			const currentUserId = state.user?.value?.id
			if (userId != currentUserId) {
				storeAPI.dispatch(addNoteToTree(note))
			}
			await checkForAndUpdateWorkspaceNoteSearches(storeAPI, note)
		})

		socket.on("DELETE_NOTE", async ({ note, articleIds, unassignedArticles, userId }) => {
			const state = storeAPI.getState()
			const currentUserId = state.user?.value?.id
			const {workspaceId} = note
			if (userId != currentUserId) {
				const relatedArticleIds = articleIds
				const closestParent = getClosestParentNoteByPath(state.workspaceNotes.byWorkspace[workspaceId].notes, note.path, true)
				await storeAPI.dispatch(fetchUniqueCounts(closestParent))
				const breadcrumbs = state.breadcrumbSlice.byWorkspace[workspaceId]?.articleMap || {}
				for (const [key] of Object.entries(breadcrumbs)) {
					if(relatedArticleIds.includes(+key)) {
						// update breadcrumb for article
						// passing in a fake article object that only includes id
						await storeAPI.dispatch(fetchArticleBreadcrumbs({article: {id: +key}, workspaceId}))
					}
				}
				storeAPI.dispatch(deleteNoteFromTree(note))
				storeAPI.dispatch(concatUnassignedArticles(unassignedArticles))
				await checkForAndUpdateWorkspaceNoteSearches(storeAPI, note)

			}
		})

		/**
		 * event handler for updates of ArticleNote relations.
		 * Listens to the /notes namespace, scoped to a workspace room.
		 *
		 * @listens INoteNamespaceEvents#ARTICLE_NOTE_RELATIONS_UPDATE
		 */
		socket.on("ARTICLE_NOTE_RELATIONS_UPDATE", async ({note, articleNotes, userId}) => {
			const state = storeAPI.getState()
			const currentUserId = getCurrentUserId(storeAPI)
			const articleNoteRelationsList = articleNotes
			const {workspaceId} = note
			if (currentUserId != userId) {
				articleNoteRelationsList.forEach(({articleId}) => {
					// reload breadcrumbs if articleMap[articleId] !== null
					if (state.breadcrumbSlice.byWorkspace[workspaceId]?.articleMap?.[articleId]) {
						storeAPI.dispatch(fetchArticleBreadcrumbs({article: {id: articleId}, note, workspaceId}))
					} else {
						// don't update crumb but update unique count
						storeAPI.dispatch(fetchUniqueCounts({id: note.id, workspaceId}))
					}
				})

			}
		})
		/**
		 * event handler for deletions of ArticleNote relations.
		 * Listens to the /notes namespace, scoped to a workspace room.
		 *
		 * @listens INoteNamespaceEvents#DELETE_ARTICLE_NOTE_RELATION
		 */
		socket.on("DELETE_ARTICLE_NOTE_RELATION", ({userId, note, articleId}) => {
			const state = storeAPI.getState()
			const {workspaceId} = note
			const currentUserId = getCurrentUserId(storeAPI)
			if (currentUserId != userId) {
				// reload breadcrumbs if articleMap[articleId] !== null
				if (state.breadcrumbSlice.byWorkspace[workspaceId].articleMap[articleId]) {
					storeAPI.dispatch(fetchArticleBreadcrumbs({article: {id: articleId}, note, workspaceId}))
				} else {
					storeAPI.dispatch(fetchUniqueCounts({id: note.id, workspaceId}))
				}
			}
		})

		socket.on("UNDO_DELETE_NOTE", async ({userId, note}) => {
			const currentUserId = getCurrentUserId(storeAPI)
			if (currentUserId != userId) {
				storeAPI.dispatch(openToPath({path: note.path, note, insertTarget: true, fromWebsocket: true}))
			}
			updateBreadcrumbs(storeAPI, note)
			await checkForAndUpdateWorkspaceNoteSearches(storeAPI, note)
		})

		socket.on("MOVE_NOTE", async ({receivingNote, movingNote, newNote, userId, movingNotePreviousLocation, workspaceId}) => {
			//receivingNote: the new parent of newNote. Does not change depending on if "paste as sibling" or "paste as child" is selected.
			const state = storeAPI.getState()
			const currentUserId = getCurrentUserId(storeAPI)
			if (currentUserId != userId) {
				const parentNote = getNoteByPath(state.workspaceNotes.byWorkspace[workspaceId].notes, receivingNote ? receivingNote.path : "")
				// is receiving note parent in the current state? 
				// is moving note in memory?
				// if receiving note parent is in memory but the moving note is nte 
				let incomingChildNotes = []
				if (parentNote && !parentNote.showChildren && parentNote.childCount > Object.keys(parentNote.noteChildren || {}).length) {
					const {data: children } = await axios.get(`/api/workspace/${workspaceId}/note/${parentNote.id || 0}/children`)
					incomingChildNotes = children
				}

				// update unique counts for all parent notes in new location
				if (parentNote) {
					// update receiving parent note hierarchy
					storeAPI.dispatch(fetchUniqueCounts({id: parentNote.id, workspaceId}))
				} else if(receivingNote) {
					// get closest parent and update count
					const closestParent = getClosestParentNoteByPath(state.workspaceNotes.byWorkspace[workspaceId].notes, receivingNote.path, true)

					storeAPI.dispatch(fetchUniqueCounts({id: closestParent.id, workspaceId}))
				}
				// update original parent note hierarchy
				const parentPath = movingNote.path.split(".").slice(0, -1).join(".")
				const originalParent = getNoteByPath(state.workspaceNotes.byWorkspace[workspaceId].notes, parentPath)
				await storeAPI.dispatch(fetchUniqueCounts({id: originalParent.id, workspaceId}))
				// {newNote, originalNote, incomingChildNotes, receivingPath, originalNoteLocation}
				storeAPI.dispatch(moveNoteInTree( {newNote, originalNote: movingNote, incomingChildNotes, receivingPath: receivingNote?.path ? receivingNote.path : "", originalNoteLocation: movingNotePreviousLocation, workspaceId}))
			} 
			updateBreadcrumbs(storeAPI, movingNote)
			await checkForAndUpdateWorkspaceNoteSearches(storeAPI, note)
		})

		return next => action => {
			if (action.type === "WS_CONNECT") {
				socket.connect()
			}
	
			if (action.type === "WS_DISCONNECT") {
				socket.disconnect()
			}
	
			if (action.type === "WS_EMIT") {
				const { event, payload } = action
				socket.emit(event, payload)
			}

			if (
				action.type === openPrimaryWorkspace.fulfilled.type ||
				action.type === openSecondaryWorkspace.fulfilled.type
			) {
				const workspaceId = action.payload.workspace.id
				socket.emit("SUBSCRIBE_TO_NOTES", workspaceId)
			}
	
			return next(action)
		}
	}
}